import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { useState, useEffect } from "react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AuthLayout from "@/layouts/auth-layout"
import { signInPath, signUpPath } from "@/routes"

export default function Register() {
  const { t, i18n } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState<string>("")

  useEffect(() => {
    const supportedLanguages = [
      "es",
      "en",
      "zh-CN",
      "pt-BR",
      "it",
      "fr",
      "ja",
      "ko",
      "am",
    ]

    // 1. Prefer the locale already saved in localStorage (set via home page picker/modal)
    const stored = localStorage.getItem("i18nextLng")
    if (stored && supportedLanguages.includes(stored)) {
      setSelectedLanguage(stored)
      i18n.changeLanguage(stored)
      return
    }

    // 2. Fall back to browser navigator language
    const fullLang = navigator.language
    const shortLang = navigator.language.split("-")[0]
    let detectedLang = "es"
    if (supportedLanguages.includes(fullLang)) {
      detectedLang = fullLang
    } else if (supportedLanguages.includes(shortLang)) {
      detectedLang = shortLang
    }

    setSelectedLanguage(detectedLang)
    i18n.changeLanguage(detectedLang)
  }, [])

  return (
    <AuthLayout
      title={t("auth.sign_up.title")}
      description={t("auth.sign_up.description")}
    >
      <Head title={t("auth.sign_up.page_title")} />
      <Form
        method="post"
        action={signUpPath()}
        resetOnSuccess={["password", "password_confirmation"]}
        disableWhileProcessing
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="name">{t("auth.sign_up.name_label")}</Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  required
                  autoFocus
                  tabIndex={1}
                  autoComplete="name"
                  disabled={processing}
                  placeholder={t("auth.sign_up.name_placeholder")}
                />
                <InputError message={errors.name} className="mt-2" />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">{t("auth.sign_up.email_label")}</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  required
                  tabIndex={2}
                  autoComplete="email"
                  placeholder={t("auth.sign_up.email_placeholder")}
                />
                <InputError message={errors.email} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">
                  {t("auth.sign_up.password_label")}
                </Label>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  required
                  tabIndex={3}
                  autoComplete="new-password"
                  placeholder={t("auth.sign_up.password_placeholder")}
                />
                <InputError message={errors.password} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password_confirmation">
                  {t("auth.sign_up.password_confirmation_label")}
                </Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  name="password_confirmation"
                  required
                  tabIndex={4}
                  autoComplete="new-password"
                  placeholder={t(
                    "auth.sign_up.password_confirmation_placeholder",
                  )}
                />
                <InputError message={errors.password_confirmation} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="language">
                  {t("auth.sign_up.language_label")}
                </Label>
                <Select
                  name="language"
                  value={selectedLanguage}
                  onValueChange={(value) => {
                    setSelectedLanguage(value)
                    i18n.changeLanguage(value)
                  }}
                >
                  <SelectTrigger id="language" tabIndex={5}>
                    <SelectValue
                      placeholder={t("auth.sign_up.language_placeholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="zh-CN">中文 (简体)</SelectItem>
                    <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="ja">日本語 (Japanese)</SelectItem>
                    <SelectItem value="ko">한국어 (Korean)</SelectItem>
                    <SelectItem value="am">አማርኛ (Amharic)</SelectItem>
                  </SelectContent>
                </Select>
                <InputError message={errors.language} />
              </div>

              <Button type="submit" className="mt-2 w-full" tabIndex={6}>
                {processing && <Spinner />}
                {t("auth.sign_up.submit")}
              </Button>
            </div>

            <div className="text-muted-foreground text-center text-sm">
              {t("auth.sign_up.have_account")}{" "}
              <TextLink href={signInPath()} tabIndex={7}>
                {t("auth.sign_up.sign_in_link")}
              </TextLink>
            </div>
          </>
        )}
      </Form>
    </AuthLayout>
  )
}
